/**
 * Impact's `facet:update` swap replaces the section's custom elements, so state
 * that must survive it lives at module scope.
 */

const STORAGE_KEY = 'maerkbare:size-preference'
const AVAILABILITY_PARAM = 'filter.v.availability'
const LINK_SELECTOR =
  '[data-scope-link], .shopify-section-group-header-group a[href*="/collections/"]'
const LOCK_CLASS = 'lock'
const MAX_STORED_SIZES = 10

const pagePreference = {
  authoritative: false,
  param: '',
  values: ([]),
}

const availabilityPreference = { initialized: false, explicit: false }

const barFocus = { pending: false, id: '', name: '', value: '' }
const stockCounts = new Map()
let stockRequestSeq = 0

const sizeRowScroll = { pending: false, left: 0 }
const sizeRowDisabled = { pending: false, values: new Set() }
let barFocusObserver = null
let barFocusTimer = null

function readStoredSizes(param) {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const stored = JSON.parse(raw)
    if (stored.param !== param || !Array.isArray(stored.values)) return []
    return normalizeSizes(
      stored.values.filter((value) => typeof value === 'string')
    ).slice(0, MAX_STORED_SIZES)
  } catch {
    return []
  }
}

function readStoredLabels(param) {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const stored = JSON.parse(raw)
    if (stored.param !== param || typeof stored.labels !== 'object' || !stored.labels) return {}
    return Object.fromEntries(
      Object.entries(stored.labels).filter(
        ([value, label]) => typeof value === 'string' && typeof label === 'string'
      )
    )
  } catch {
    return {}
  }
}

function renderedSizeLabels() {
  const labels = {}
  for (const option of document.querySelectorAll('[data-size-label]')) {
    const input = option.querySelector('input')
    const label = option.getAttribute('data-size-label')
    if (input instanceof HTMLInputElement && label) labels[input.value] = label
  }
  return labels
}

function storageWritesAllowed() {
  const consent = window.Shopify?.customerPrivacy?.currentVisitorConsent?.()
  return consent?.preferences !== 'no'
}

function writeStoredSizes(param, values) {
  try {
    const normalizedValues = normalizeSizes(values).slice(0, MAX_STORED_SIZES)
    if (normalizedValues.length === 0) {
      window.localStorage.removeItem(STORAGE_KEY)
      return
    }
    if (!storageWritesAllowed()) return
    const known = { ...readStoredLabels(param), ...renderedSizeLabels() }
    const preference = {
      param,
      values: normalizedValues,
      labels: Object.fromEntries(
        normalizedValues.filter((value) => known[value]).map((value) => [value, known[value]])
      ),
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preference))
  } catch {
    return
  }
}

function normalizeSizes(values) {
  return [
    ...new Set(
      values
        .filter((value) => typeof value === 'string')
        .map((value) => value.trim())
        .filter(Boolean)
    ),
  ].sort()
}

function sizesInUrl(url, param) {
  return normalizeSizes(url.searchParams.getAll(param))
}

function hasAvailability(url) {
  return url.searchParams.getAll(AVAILABILITY_PARAM).includes('1')
}

function coupledAvailabilityUrl(url, sizeParam) {
  const coupled = new URL(url)
  const includeAvailability =
    sizesInUrl(coupled, sizeParam).length > 0 ||
    availabilityPreference.explicit
  coupled.searchParams.delete(AVAILABILITY_PARAM)
  if (includeAvailability) coupled.searchParams.set(AVAILABILITY_PARAM, '1')
  return coupled
}

function initializeAvailabilityPreference(url, sizeParam) {
  if (availabilityPreference.initialized) return
  availabilityPreference.initialized = true
  availabilityPreference.explicit =
    hasAvailability(url) && sizesInUrl(url, sizeParam).length === 0
}

function decorateLinks(param, values) {
  for (const link of document.querySelectorAll(LINK_SELECTOR)) {
    if (!(link instanceof HTMLAnchorElement)) continue
    const url = new URL(link.href, window.location.origin)
    if (url.origin !== window.location.origin) continue
    if (!url.pathname.includes('/collections/')) continue
    url.searchParams.delete(param)
    url.searchParams.delete(AVAILABILITY_PARAM)
    for (const value of values) url.searchParams.append(param, value)
    if (values.length > 0) url.searchParams.set(AVAILABILITY_PARAM, '1')
    link.href = url.toString()
  }
}

function captureAvailabilityPreference(event) {
  const input = event.target
  if (!(input instanceof HTMLInputElement)) return
  if (!input.matches('[data-explicit-availability]')) return
  availabilityPreference.explicit = input.checked
}

function coupleAvailabilityInput(form, sizeParam) {
  for (const input of form.querySelectorAll(
    `input[type="hidden"][name="${AVAILABILITY_PARAM}"]`
  )) {
    input.remove()
  }

  const hasSize = form.querySelector(
    `input[type="checkbox"][name="${sizeParam}"]:checked`
  )
  const explicitInput = form.querySelector('[data-explicit-availability]')
  const explicitInputChecked =
    explicitInput instanceof HTMLInputElement &&
    explicitInput.checked &&
    !explicitInput.disabled
  if (!hasSize && !availabilityPreference.explicit) return
  if (explicitInputChecked) return

  const implicitInput = document.createElement('input')
  implicitInput.type = 'hidden'
  implicitInput.name = AVAILABILITY_PARAM
  implicitInput.value = '1'
  implicitInput.dataset.sizeAvailability = ''
  form.append(implicitInput)
}

function prepareFilterSubmission(event, sizeParam) {
  const form = event.target
  if (!(form instanceof HTMLFormElement)) return
  if (!form.matches('.filter-bar__form, .filter-drawer__form')) return
  coupleAvailabilityInput(form, sizeParam)
}

function captureBarFocus(activeElement) {
  if (barFocusTimer !== null) window.clearTimeout(barFocusTimer)
  barFocusTimer = null
  barFocus.pending = false
  barFocus.id = ''
  barFocus.name = ''
  barFocus.value = ''
  if (!activeElement?.closest('.filter-bar')) return

  const sort = activeElement.closest('facet-sort-by')
  const target = sort?.querySelector('[aria-controls]') ?? activeElement
  if (!(target instanceof HTMLElement)) return

  barFocus.pending = true
  barFocus.id = target.id
  barFocus.name = target.getAttribute('name') ?? ''
  barFocus.value = target.getAttribute('value') ?? ''
}

function restoreBarFocus() {
  if (!barFocus.pending) return

  let target = barFocus.id ? document.getElementById(barFocus.id) : null
  if (!(target instanceof HTMLElement) && barFocus.name) {
    for (const candidate of document.querySelectorAll('[name]')) {
      if (!(candidate instanceof HTMLElement)) continue
      if (candidate.getAttribute('name') !== barFocus.name) continue
      if (candidate.getAttribute('value') !== barFocus.value) continue
      target = candidate
      break
    }
  }
  if (!(target instanceof HTMLElement)) return
  target.focus({ preventScroll: true })
  if (barFocusTimer !== null) window.clearTimeout(barFocusTimer)
  barFocusTimer = window.setTimeout(() => {
    barFocus.pending = false
    barFocusObserver?.disconnect()
    barFocusObserver = null
    barFocusTimer = null
  }, 1000)
}

function watchForBarFocusReplacement(activeElement) {
  const section = activeElement?.closest('.shopify-section')
  if (!section || !barFocus.pending) return
  barFocusObserver?.disconnect()
  barFocusObserver = new MutationObserver(() => {
    if (!barFocus.pending) return
    window.requestAnimationFrame(restoreBarFocus)
  })
  barFocusObserver.observe(section, { childList: true })
}

function captureChangedFilterFocus(event) {
  const target = event.target
  if (!(target instanceof HTMLElement) || !target.closest('.filter-bar')) return
  captureBarFocus(target)
  watchForBarFocusReplacement(target)
}

function sizeNumber(label) {
  const match = label
    .trim()
    .replace(',', '.')
    .match(/\d+(?:\.\d+)?/)
  return match ? Number.parseFloat(match[0]) : null
}

function updateSizeOverflow(list) {
  const maxScrollLeft = list.scrollWidth - list.clientWidth
  const position = Math.abs(list.scrollLeft)
  const hasPrevious = maxScrollLeft > 1 && position > 1
  const hasMore = maxScrollLeft > 1 && position < maxScrollLeft - 1

  const scroller = list.closest('.size-row__scroller')
  if (!(scroller instanceof HTMLElement)) return
  const previous = scroller.querySelector('[data-size-scroll="backward"]')
  const next = scroller.querySelector('[data-size-scroll="forward"]')
  if (previous instanceof HTMLButtonElement) previous.hidden = !hasPrevious
  if (next instanceof HTMLButtonElement) next.hidden = !hasMore
}

function updateSizeOverflowStates() {
  for (const list of document.querySelectorAll('.size-row__list')) {
    if (list instanceof HTMLElement) updateSizeOverflow(list)
  }
}

function scrollSizeRow(event) {
  const target = event.target
  if (!(target instanceof Element)) return
  const button = target.closest('[data-size-scroll]')
  if (!(button instanceof HTMLButtonElement)) return
  const scroller = button.closest('.size-row__scroller')
  const list = scroller?.querySelector('.size-row__list')
  if (!(list instanceof HTMLElement)) return
  const direction = button.dataset.sizeScroll === 'backward' ? -1 : 1
  const rtlMultiplier = getComputedStyle(list).direction === 'rtl' ? -1 : 1
  list.scrollBy({
    left: direction * rtlMultiplier * list.clientWidth,
    behavior: 'smooth',
  })
}

function syncAvailabilitySwitches() {
  for (const input of document.querySelectorAll(
    '[data-explicit-availability]'
  )) {
    if (input instanceof HTMLInputElement && !input.disabled) {
      input.checked = availabilityPreference.explicit
    }
  }
}

function sizeLegendLabel() {
  const rendered = document.querySelector(
    '.size-row__legend--full, [data-size-legend]'
  )
  if (!(rendered instanceof HTMLElement)) return undefined
  return rendered.dataset.sizeLegend || rendered.textContent?.trim() || undefined
}

function reconcileSizeChips(param) {
  const url = new URL(window.location.href)
  const active = sizesInUrl(url, param)
  if (active.length === 0) return
  const labels = { ...readStoredLabels(param), ...renderedSizeLabels() }
  const fallbackLabel = sizeLegendLabel() ?? param
  const labelFor = (value) => labels[value] ?? fallbackLabel

  for (const list of document.querySelectorAll(
    '.size-row__list, [data-size-options]'
  )) {
    if (!(list instanceof HTMLElement)) continue
    const isBarList = list.classList.contains('size-row__list')
    const template = list.querySelector(
      isBarList ? '.size-row__option' : '[data-size-option]'
    )
    if (!(template instanceof HTMLElement)) continue
    const rendered = new Set(
      [...list.querySelectorAll(`input[name="${param}"]`)].map((input) =>
        input instanceof HTMLInputElement ? input.value : ''
      )
    )
    for (const value of active) {
      if (rendered.has(value)) continue
      const option = (template.cloneNode(true))
      const input = option.querySelector('input')
      const label = option.querySelector('label')
      if (!(input instanceof HTMLInputElement) || !(label instanceof HTMLElement)) continue
      const id = `${input.id}-url-${value.replace(/[^\w.-]/g, '')}`
      option.dataset.sizeLabel = labelFor(value)
      input.id = id
      input.name = param
      input.value = value
      input.checked = true
      input.disabled = false
      label.htmlFor = id
      label.textContent = labelFor(value)
      list.append(option)
    }
  }

  const emptyFilters = document.querySelector('[data-empty-active-filters]')
  if (!(emptyFilters instanceof HTMLElement)) return
  const shown = new Set(
    [...emptyFilters.querySelectorAll('.removable-facet')].map((facet) =>
      (facet.firstChild?.textContent ?? '').trim()
    )
  )
  let container = emptyFilters.querySelector('.active-facets')
  if (!container) {
    container = document.createElement('div')
    container.className = 'active-facets'
    emptyFilters.append(container)
  }
  for (const value of active) {
    if (shown.has(labelFor(value))) continue
    const removeUrl = new URL(url)
    const remaining = active.filter((size) => size !== value)
    removeUrl.searchParams.delete(param)
    for (const size of remaining) removeUrl.searchParams.append(param, size)
    const facet = document.createElement('div')
    facet.className = 'removable-facet'
    facet.append(document.createTextNode(labelFor(value)))
    const link = document.createElement('a', { is: 'facet-link' })
    link.href = removeUrl.toString()
    link.className = 'tap-area'
    link.setAttribute('is', 'facet-link')
    link.setAttribute('data-no-instant', '')
    link.setAttribute(
      'aria-label',
      (emptyFilters.dataset.removeLabel ?? '__NAME__').replace('__NAME__', labelFor(value))
    )
    const icon = document.querySelector('.removable-facet a svg')
    if (icon) link.append(icon.cloneNode(true))
    else link.textContent = '×'
    facet.append(link)
    container.prepend(facet)
  }
}

const STOCK_DISABLED_ATTR = 'data-stock-disabled'

function sizeInputs() {
  return [
    ...document.querySelectorAll('.size-row__input, [data-size-option] input'),
  ].filter((input) => input instanceof HTMLInputElement)
}

function setStockDisabled(input, disabled) {
  input.disabled = disabled
  input.toggleAttribute(STOCK_DISABLED_ATTR, disabled)
}

function applyCarriedDisabled(url) {
  if (!sizeRowDisabled.pending) return
  sizeRowDisabled.pending = false
  if (url.searchParams.has(AVAILABILITY_PARAM)) return
  for (const input of sizeInputs()) {
    if (input.checked || input.disabled) continue
    if (sizeRowDisabled.values.has(input.value)) setStockDisabled(input, true)
  }
}

function markSizesByStock(url) {
  if (url.searchParams.has(AVAILABILITY_PARAM)) return
  const inputs = sizeInputs()
  if (inputs.length === 0) return
  const param = inputs[0].name
  const query = new URL(url)
  query.searchParams.delete('page')
  query.searchParams.set(AVAILABILITY_PARAM, '1')
  query.searchParams.set('section_id', 'size-availability')
  const key = query.search
  const seq = ++stockRequestSeq

  let pending = stockCounts.get(key)
  if (!pending) {
    pending = fetchStockCounts(query)
    stockCounts.set(key, pending)
    pending.catch(() => stockCounts.delete(key))
  }

  pending.then(
    (counts) => {
      if (seq === stockRequestSeq) applyStockCounts(counts[param] ?? {})
    },
    (error) => {
      if (seq === stockRequestSeq) console.error('Size availability lookup failed', error)
    }
  )
}

async function fetchStockCounts(query) {
  const response = await fetch(query.pathname + query.search, {
    priority: 'low',
    credentials: 'same-origin',
  })
  if (!response.ok) {
    throw new Error(`size-availability responded ${response.status}`)
  }
  const html = await response.text()
  const match = html.match(
    /<script[^>]*data-size-availability[^>]*>([\s\S]*?)<\/script>/
  )
  if (!match) throw new Error('size-availability rendered without its JSON payload')
  return JSON.parse(match[1])
}

function applyStockCounts(counts) {
  for (const input of sizeInputs()) {
    if (input.checked) continue
    const inStock = (counts[input.value] ?? 0) > 0
    if (!inStock && !input.disabled) {
      setStockDisabled(input, true)
    } else if (inStock && input.hasAttribute(STOCK_DISABLED_ATTR)) {
      setStockDisabled(input, false)
    }
  }
}

function organizeSizeRows() {
  for (const list of document.querySelectorAll(
    '.size-row__list, [data-size-options]'
  )) {
    if (!(list instanceof HTMLElement)) continue
    const isBarList = list.classList.contains('size-row__list')
    const optionSelector = isBarList
      ? '.size-row__option'
      : '[data-size-option]'
    const options = [...list.querySelectorAll(optionSelector)].map(
      (option, index) => ({
        element: option,
        index,
        number: sizeNumber(option.getAttribute('data-size-label') ?? ''),
      })
    )
    options.sort((left, right) => {
      const leftGroup = left.number === null ? 1 : 0
      const rightGroup = right.number === null ? 1 : 0
      if (leftGroup !== rightGroup) return leftGroup - rightGroup
      if (left.number !== null && right.number !== null) {
        const numericOrder = left.number - right.number
        if (numericOrder !== 0) return numericOrder
      }
      return left.index - right.index
    })

    for (const option of options) list.append(option.element)
    if (!isBarList) continue
    const initial = list.querySelector('.size-row__option--initial')
    if (sizeRowScroll.pending) {
      initial?.classList.remove('size-row__option--initial')
      sizeRowScroll.pending = false
      list.scrollLeft = sizeRowScroll.left
      updateSizeOverflow(list)
      continue
    }
    const selected = list.querySelector(
      '.size-row__input:checked + .size-row__label'
    )
    const scroller = list
    if (
      !(selected instanceof HTMLElement) ||
      !(scroller instanceof HTMLElement)
    ) {
      if (
        initial instanceof HTMLElement &&
        !CSS.supports('scroll-initial-target', 'nearest')
      ) {
        list.scrollLeft =
          initial.offsetLeft + initial.offsetWidth / 2 - list.clientWidth / 2
      }
      initial?.classList.remove('size-row__option--initial')
      updateSizeOverflow(list)
      continue
    }
    list.scrollLeft = 0
    updateSizeOverflow(list)
    const selectedRect = selected.getBoundingClientRect()
    const scrollerRect = scroller.getBoundingClientRect()
    if (
      selectedRect.left < scrollerRect.left ||
      selectedRect.right > scrollerRect.right
    ) {
      selected.scrollIntoView({ block: 'nearest', inline: 'center' })
    }
  }
}

function positionFilterOverlay(overlay) {
  const isFacetDialog = overlay.matches('facet-dialog')
  const isDesktopPopover =
    overlay.matches('x-popover') &&
    window.matchMedia('(min-width: 1000px)').matches
  if (!isFacetDialog && !isDesktopPopover) return

  let trigger = null
  for (const candidate of document.querySelectorAll('[aria-controls]')) {
    if (!(candidate instanceof HTMLElement)) continue
    if (candidate.getAttribute('aria-controls') !== overlay.id) continue
    trigger = candidate
    break
  }
  if (!trigger) return

  overlay.classList.add('filter-bar__floating-dialog')
  const margin = 12
  const gap = 12
  const triggerRect = trigger.getBoundingClientRect()
  const overlayRect = overlay.getBoundingClientRect()
  const left = Math.max(
    margin,
    Math.min(triggerRect.left, window.innerWidth - overlayRect.width - margin)
  )
  const below = triggerRect.bottom + gap
  const top =
    below + overlayRect.height <= window.innerHeight - margin
      ? below
      : Math.max(margin, triggerRect.top - overlayRect.height - gap)

  overlay.style.left = `${left}px`
  overlay.style.right = 'auto'
  overlay.style.top = `${top}px`
  overlay.style.bottom = 'auto'
}

function positionToggledFilterOverlay(event) {
  const overlay = event.target
  if (!(overlay instanceof HTMLElement) || !overlay.hasAttribute('open')) return
  if (!overlay.closest('.filter-bar')) return
  positionFilterOverlay(overlay)
}

function repositionOpenFilterOverlays() {
  for (const overlay of document.querySelectorAll(
    '.filter-bar__floating-dialog[open]'
  )) {
    if (overlay instanceof HTMLElement) positionFilterOverlay(overlay)
  }
  updateSizeOverflowStates()
}

class SizePreferenceElement extends HTMLElement {
  #controller = null

  connectedCallback() {
    this.#controller?.abort()
    this.#controller = new AbortController()
    const { signal } = this.#controller
    const param = this.param

    const currentUrl = new URL(window.location.href)
    initializeAvailabilityPreference(currentUrl, param)
    syncAvailabilitySwitches()
    this.#syncFromUrl(currentUrl, pagePreference.param === param)
    queueMicrotask(() => {
      applyCarriedDisabled(currentUrl)
      reconcileSizeChips(param)
      organizeSizeRows()
      window.requestAnimationFrame(restoreBarFocus)
      markSizesByStock(currentUrl)
    })

    this.#bindFilterUi(signal, param)
    document.addEventListener('facet:update', (event) => this.#onFacetUpdate(event), {
      signal,
    })
    window.addEventListener(
      'pageshow',
      (event) => {
        if (event.persisted) this.#resync()
      },
      { signal }
    )
    this.#normalizeInitialAvailability(currentUrl)
    window.addEventListener(
      'storage',
      (event) => {
        if (event.key === STORAGE_KEY || event.key === null) this.#resync()
      },
      { signal }
    )
  }

  #bindFilterUi(signal, param) {
    document.addEventListener('toggle', positionToggledFilterOverlay, {
      signal,
    })
    document.addEventListener('click', scrollSizeRow, { signal })
    document.addEventListener('change', captureChangedFilterFocus, {
      capture: true,
      signal,
    })
    document.addEventListener('change', captureAvailabilityPreference, {
      capture: true,
      signal,
    })
    document.addEventListener(
      'submit',
      (event) => prepareFilterSubmission(event, param),
      { capture: true, signal }
    )
    document.addEventListener('scroll', repositionOpenFilterOverlays, {
      capture: true,
      signal,
    })
    window.addEventListener('resize', repositionOpenFilterOverlays, { signal })
  }

  #syncFromUrl(url, pageIsAuthoritative) {
    if (url.searchParams.has(this.param)) {
      this.#applyUrl(url)
    } else if (pageIsAuthoritative) {
      decorateLinks(this.param, pagePreference.values)
    } else {
      this.#applyStored()
    }
  }

  #resync() {
    syncAvailabilitySwitches()
    this.#syncFromUrl(new URL(window.location.href), pagePreference.authoritative)
  }

  #onFacetUpdate(event) {
    const url = (event).detail?.url
    if (!(url instanceof URL)) return
    syncAvailabilitySwitches()
    if (!url.searchParams.getAll(AVAILABILITY_PARAM).includes('1')) {
      availabilityPreference.explicit = false
    }
    const sizeList = document.querySelector('.size-row__list')
    if (sizeList instanceof HTMLElement) {
      sizeRowScroll.pending = true
      sizeRowScroll.left = sizeList.scrollLeft
      sizeRowDisabled.pending = true
      sizeRowDisabled.values = new Set(
        [...sizeList.querySelectorAll('.size-row__input:disabled')].map(
          (input) => (input instanceof HTMLInputElement ? input.value : '')
        )
      )
    }
    const activeElement =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null
    captureBarFocus(activeElement)
    watchForBarFocusReplacement(activeElement)
    this.#applyUrl(url)
  }

  disconnectedCallback() {
    this.#controller?.abort()
    this.#controller = null
  }

  get param() {
    return this.getAttribute('param') ?? 'filter.v.m.barefoot.shoe_size_eu'
  }

  #applyUrl(url) {
    const values = sizesInUrl(url, this.param)
    pagePreference.authoritative = true
    pagePreference.param = this.param
    pagePreference.values = values
    writeStoredSizes(this.param, values)
    decorateLinks(this.param, values)
  }

  #applyStored() {
    const values = readStoredSizes(this.param)
    pagePreference.authoritative = false
    pagePreference.param = this.param
    pagePreference.values = values
    decorateLinks(this.param, values)
  }

  #normalizeInitialAvailability(url) {
    const normalized = coupledAvailabilityUrl(url, this.param)
    if (normalized.search === url.search) return
    const section = this.closest('.shopify-section')
    const sectionId = section?.id.replace('shopify-section-', '') ?? ''
    if (!sectionId) {
      window.history.replaceState({}, '', normalized)
      return
    }
    normalized.searchParams.set('section_id', sectionId)
    queueMicrotask(() => {
      if (!this.isConnected) return
      this.dispatchEvent(
        new CustomEvent('facet:update', {
          bubbles: true,
          detail: { url: normalized },
        })
      )
    })
  }
}

const reopen = { pending: false, focusId: '', scrollTop: 0 }
/** A closed dialog is display:none, which resets its scroll offset. */
let drawerScrollTop = 0

class FilterDrawerElement extends HTMLElement {
  #controller = null
  #opener = null
  #closeTimer = null
  #locked = false
  #ownsLock = false

  connectedCallback() {
    const dialog = this.dialog
    if (!dialog) return
    this.#controller?.abort()
    this.#controller = new AbortController()
    const { signal } = this.#controller

    for (const trigger of this.#triggers()) {
      trigger.addEventListener('click', () => this.open(trigger), { signal })
    }
    for (const closer of dialog.querySelectorAll('[data-drawer-close]')) {
      closer.addEventListener('click', () => this.close(), { signal })
    }
    for (const clearer of dialog.querySelectorAll('[data-filter-clear]')) {
      clearer.addEventListener('click', () => this.#clearFilters(clearer), {
        signal,
      })
    }
    dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === dialog) this.close()
      },
      { signal }
    )
    dialog.addEventListener(
      'cancel',
      (event) => {
        event.preventDefault()
        this.close()
      },
      { signal }
    )
    dialog.addEventListener('close', () => this.#onClose(), { signal })
    dialog.addEventListener(
      'facet:update',
      () => {
        reopen.focusId = document.activeElement?.id ?? ''
        reopen.scrollTop = this.body?.scrollTop ?? 0
      },
      { signal }
    )
    if (reopen.pending) this.#restore()
  }

  disconnectedCallback() {
    const dialog = this.dialog
    reopen.pending = dialog?.open ?? false
    if (this.#closeTimer !== null) window.clearTimeout(this.#closeTimer)
    this.#closeTimer = null
    this.#controller?.abort()
    this.#controller = null
    this.#unlock()
  }

  get dialog() {
    return this.querySelector('dialog')
  }

  get body() {
    return this.querySelector('.filter-drawer__body')
  }

  get sectionId() {
    return this.getAttribute('section-id') ?? ''
  }

  open(opener) {
    const dialog = this.dialog
    if (!dialog || dialog.open) return
    this.#opener = opener ?? null
    delete dialog.dataset.closing
    delete dialog.dataset.noMotion
    dialog.showModal()
    if (this.body) this.body.scrollTop = drawerScrollTop
    this.#lock()
    this.#setExpanded(true)
    const close = dialog.querySelector('[data-drawer-close]')
    close?.focus({ preventScroll: true })
  }

  close() {
    const dialog = this.dialog
    if (!dialog?.open || dialog.dataset.closing !== undefined) return
    drawerScrollTop = this.body?.scrollTop ?? 0
    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches
    if (reducedMotion) {
      dialog.close()
      return
    }
    delete dialog.dataset.noMotion
    dialog.dataset.closing = ''
    this.#closeTimer = window.setTimeout(() => {
      this.#closeTimer = null
      if (dialog.open) dialog.close()
    }, 250)
  }

  #triggers() {
    const dialog = this.dialog
    if (!dialog?.id) return []
    return [
      ...document.querySelectorAll(`[aria-controls="${dialog.id}"]`),
    ].filter((element) => element instanceof HTMLElement)
  }

  #setExpanded(expanded) {
    for (const trigger of this.#triggers()) {
      trigger.setAttribute('aria-expanded', String(expanded))
    }
  }

  #lock() {
    this.#locked = true
    this.#ownsLock = !document.documentElement.classList.contains(LOCK_CLASS)
    if (this.#ownsLock) document.documentElement.classList.add(LOCK_CLASS)
  }

  #unlock() {
    if (!this.#locked) return
    this.#locked = false
    if (this.#ownsLock) document.documentElement.classList.remove(LOCK_CLASS)
    this.#ownsLock = false
  }

  #onClose() {
    reopen.pending = false
    delete this.dialog?.dataset.closing
    delete this.dialog?.dataset.noMotion
    this.#unlock()
    this.#setExpanded(false)
    this.#opener?.focus()
    this.#opener = null
  }

  #clearFilters(source) {
    availabilityPreference.explicit = false
    const url = new URL(window.location.href)
    for (const param of [...new Set(url.searchParams.keys())]) {
      if (param.startsWith('filter.')) url.searchParams.delete(param)
    }
    url.searchParams.delete('page')
    if (this.sectionId) url.searchParams.set('section_id', this.sectionId)
    source.dispatchEvent(
      new CustomEvent('facet:update', {
        bubbles: true,
        detail: { url },
      })
    )
  }

  #restore() {
    reopen.pending = false
    const dialog = this.dialog
    if (!dialog) return
    try {
      dialog.dataset.noMotion = ''
      dialog.showModal()
    } catch {
      return
    }
    this.#lock()
    this.#setExpanded(true)
    if (this.body) this.body.scrollTop = reopen.scrollTop
    const focusTarget = reopen.focusId
      ? document.getElementById(reopen.focusId)
      : null
    const fallback = dialog.querySelector('[data-drawer-close]')
    if (focusTarget instanceof HTMLElement) {
      focusTarget.focus()
    } else if (fallback instanceof HTMLElement) {
      fallback.focus()
    }
    this.#opener = this.#triggers()[0] ?? null
  }
}

if (!customElements.get('mb-size-preference')) {
  customElements.define('mb-size-preference', SizePreferenceElement)
}
if (!customElements.get('mb-filter-drawer')) {
  customElements.define('mb-filter-drawer', FilterDrawerElement)
}
